import React, { useEffect, useState } from 'react';
import { BarChart3, Download, Calendar, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

export function CMSMarketAnalysis() {
  const [isLoading, setIsLoading] = useState(true);
  const [scriptError, setScriptError] = useState(false);

  useEffect(() => {
    // Load Tableau JavaScript API
    const script = document.createElement('script');
    script.src = 'https://public.tableau.com/javascripts/api/viz_v1.js';
    script.async = true;

    script.onload = () => {
      console.log('✅ Tableau script loaded successfully');
      setIsLoading(false);
    };

    script.onerror = (error) => {
      console.error('❌ Failed to load Tableau script:', error);
      setScriptError(true);
      setIsLoading(false);
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup - remove script on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleExport = () => {
    console.log('📊 Export report clicked');
    // Add export functionality here if needed
  };

  const handleSchedule = () => {
    console.log('📅 Schedule review clicked');
    // Add schedule functionality here if needed
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Page Header */}
      <div className="border-b bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">CMS Market Analysis</h1>
              <p className="text-sm text-muted-foreground">
                Georgia Healthcare Market Intelligence | Data: December 2025
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={handleExport}
              className="flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export Report</span>
            </Button>
            <Button
              onClick={handleSchedule}
              className="flex items-center space-x-2"
            >
              <Calendar className="w-4 h-4" />
              <span>Schedule Review</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Dashboard Container */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-[1920px] mx-auto">
          {/* Loading State */}
          {isLoading && !scriptError && (
            <div className="flex flex-col items-center justify-center h-96">
              <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4"></div>
              <p className="text-muted-foreground">Loading market intelligence dashboard...</p>
            </div>
          )}

          {/* Error State */}
          {scriptError && (
            <div className="flex items-center justify-center h-96">
              <div className="text-center p-8 bg-red-50 border border-red-200 rounded-lg max-w-md">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-red-900 mb-2">Failed to Load Dashboard</h2>
                <p className="text-red-700 mb-4">
                  Unable to load the Tableau visualization script. Please check your internet connection and try again.
                </p>
                <Button onClick={() => window.location.reload()}>
                  Reload Page
                </Button>
              </div>
            </div>
          )}

          {/* Tableau Embed */}
          {!scriptError && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden" style={{ minHeight: '800px' }}>
              <div
                className='tableauPlaceholder'
                id='viz1767680102409'
                style={{ position: 'relative', width: '100%', height: '100%' }}
              >
                <noscript>
                  <a href='#'>
                    <img
                      alt='GEORGIA HEALTHCARE MARKET INTELLIGENCE'
                      src='https://public.tableau.com/static/images/GE/GEORGIAHEALTHCAREMARKETINTELLIGENCE/Dashboard1/1_rss.png'
                      style={{ border: 'none', width: '100%' }}
                    />
                  </a>
                </noscript>
                <object className='tableauViz' style={{ width: '100%', height: '800px' }}>
                  <param name='host_url' value='https%3A%2F%2Fpublic.tableau.com%2F' />
                  <param name='embed_code_version' value='3' />
                  <param name='site_root' value='' />
                  <param name='name' value='GEORGIAHEALTHCAREMARKETINTELLIGENCE/Dashboard1' />
                  <param name='tabs' value='no' />
                  <param name='toolbar' value='yes' />
                  <param name='static_image' value='https://public.tableau.com/static/images/GE/GEORGIAHEALTHCAREMARKETINTELLIGENCE/Dashboard1/1.png' />
                  <param name='animate_transition' value='yes' />
                  <param name='display_static_image' value='yes' />
                  <param name='display_spinner' value='yes' />
                  <param name='display_overlay' value='yes' />
                  <param name='display_count' value='yes' />
                  <param name='language' value='en-US' />
                </object>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t bg-white px-6 py-3">
        <div className="max-w-[1920px] mx-auto text-center space-y-1">
          <p className="text-xs text-muted-foreground">
            Data Sources: CMS Hospital Compare, CMS Medicare Advantage Enrollment (December 2025)
          </p>
          <p className="text-xs font-semibold text-yellow-600 uppercase tracking-wide">
            Confidential - For WellStar Catalyst Investment Committee
          </p>
        </div>
      </div>
    </div>
  );
}
